import { DeviceLocationFix } from '@/utils/captureBestDeviceLocation';

export const GPS_REUSE_MAX_AGE_MS = 120_000;
export const GPS_RETRY_TIMEOUT_MS = 10_000;
export const GPS_RETRY_MAX_ATTEMPTS = 3;

export type GpsRetryPhase = 'idle' | 'retrying' | 'exhausted';

export type GpsRetryState = {
  phase: GpsRetryPhase;
  attempt: number;
  secondsRemaining: number;
};

export const INITIAL_GPS_RETRY_STATE: GpsRetryState = {
  phase: 'idle',
  attempt: 0,
  secondsRemaining: 0,
};

export type GpsRetrySequenceResult = {
  successFix: DeviceLocationFix | null;
  bestFix: DeviceLocationFix | null;
};

/**
 * Returns true when the fix meets the configured minimum GPS accuracy threshold.
 */
export function isDeviceLocationPreciseEnough(
  fix: DeviceLocationFix,
  minAccuracyMetres: number
): boolean {
  if (fix.accuracyMetres == null) {
    return true;
  }
  return fix.accuracyMetres <= minAccuracyMetres;
}

/**
 * Returns true when the fix was captured within the reuse window (default 2 minutes).
 */
export function isDeviceLocationFresh(
  fix: DeviceLocationFix | null | undefined,
  now: Date = new Date(),
  maxAgeMs: number = GPS_REUSE_MAX_AGE_MS
): boolean {
  if (!fix?.gpsCapturedAt) {
    return false;
  }
  const capturedAt = Date.parse(fix.gpsCapturedAt);
  if (!Number.isFinite(capturedAt)) {
    return false;
  }
  return now.getTime() - capturedAt < maxAgeMs;
}

/**
 * Picks the fix with the best (lowest) accuracy; prefers non-null over null.
 */
export function pickBestDeviceLocationFix(
  a: DeviceLocationFix | null,
  b: DeviceLocationFix | null
): DeviceLocationFix | null {
  if (!a) {
    return b;
  }
  if (!b) {
    return a;
  }
  const accA = a.accuracyMetres;
  const accB = b.accuracyMetres;
  if (accA == null && accB == null) {
    return a;
  }
  if (accA == null) {
    return b;
  }
  if (accB == null) {
    return a;
  }
  return accB < accA ? b : a;
}

type CountdownHandle = {
  stop: () => void;
};

function startCountdown(
  totalMs: number,
  onTick: (secondsRemaining: number) => void
): CountdownHandle {
  const totalSeconds = Math.ceil(totalMs / 1000);
  let secondsRemaining = totalSeconds;
  onTick(secondsRemaining);
  const intervalId = globalThis.setInterval(() => {
    secondsRemaining -= 1;
    if (secondsRemaining >= 0) {
      onTick(secondsRemaining);
    }
  }, 1000);
  return {
    stop: () => {
      globalThis.clearInterval(intervalId);
    },
  };
}

/**
 * Runs up to `maxAttempts` sequential GPS captures, each bounded by `timeoutMs`.
 * Invokes `onAttemptTick` once per second during each attempt.
 * Returns the first successful fix, or the best fix observed across all attempts.
 */
export async function runGpsRetrySequence(params: {
  captureOnce: () => Promise<DeviceLocationFix | null>;
  minAccuracyMetres: number;
  maxAttempts?: number;
  timeoutMs?: number;
  onAttemptTick?: (attempt: number, secondsRemaining: number) => void;
}): Promise<GpsRetrySequenceResult> {
  const maxAttempts = params.maxAttempts ?? GPS_RETRY_MAX_ATTEMPTS;
  const timeoutMs = params.timeoutMs ?? GPS_RETRY_TIMEOUT_MS;
  let bestFix: DeviceLocationFix | null = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const countdown = startCountdown(timeoutMs, (secondsRemaining) => {
      params.onAttemptTick?.(attempt, secondsRemaining);
    });
    const fix = await params.captureOnce();
    countdown.stop();
    bestFix = pickBestDeviceLocationFix(bestFix, fix);
    if (fix && isDeviceLocationPreciseEnough(fix, params.minAccuracyMetres)) {
      return { successFix: fix, bestFix };
    }
  }
  return { successFix: null, bestFix };
}
